import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 161511 $
//  Purpose   : hocr rendering support
// =============================================================================
// ---Third-party imports
import React from 'react';
import clsx from 'clsx';
// ---Framework imports
import log from 'uix/logging';
// ---Local imports
import S from './styles.css';
// ---Exports
import { SOFT_HYPHEN } from './constants';
import { Popup } from 'semantic-ui-react';
import { VIcon } from 'uix/graphics';
/* currently line-mode only */ export const hocrPage = {
    match: ({ tag, attrib })=>{
        return tag == 'div' && attrib.className == 'ocr_page';
    },
    build: ({ children, applyTemplates })=>{
        return /*#__PURE__*/ _jsx("div", {
            className: S.ocrx_page,
            children: applyTemplates(children)
        });
    }
};
export const hocrBlock = {
    match: ({ tag, attrib })=>{
        return tag == 'div' && attrib.className == 'ocrx_block';
    },
    build: ({ children, applyTemplates })=>{
        return /*#__PURE__*/ _jsx("div", {
            className: S.ocrx_block,
            children: applyTemplates(children)
        });
    }
};
export const hocrLine = {
    match: ({ tag, attrib })=>tag == 'span' && attrib.className == 'ocr_line',
    build: ({ applyTemplates, children })=>{
        let last_word = children.slice(-1)[0];
        let ws = last_word?.text?.endsWith(SOFT_HYPHEN) ? '-' : ' ';
        if (last_word?.attrib && (last_word.attrib['data-vl-entity'] || last_word.attrib['data-vl-glossary'])) {
            ws = /*#__PURE__*/ _jsx("span", {
                className: S.entity,
                children: ws
            });
        }
        return /*#__PURE__*/ _jsxs("span", {
            className: S.ocr_line,
            children: [
                applyTemplates(children),
                ws
            ]
        });
    }
};
export const linkedTerm = {
    //~ matching entities and glossary,
    match: ({ tag, attrib })=>tag === 'span' && attrib['data-vl-entity'] || tag === 'span' && attrib['data-vl-glossary'],
    build: ({ data, keys, node, attrib, applyTemplates })=>{
        let { i18n } = data;
        let type = attrib['data-vl-entity'] ? 'entity' : 'glossary';
        let allTerms = keys.byTag['span'].filter(({ attrib })=>attrib[`data-vl-${type}`]);
        let idx = allTerms.findIndex((e)=>e === node);
        if (idx === -1) return null;
        let group = attrib[`data-vl-${type}-group`] || '';
        let lastInGroup = true;
        if (idx + 1 < allTerms.length && allTerms[idx + 1].attrib[`data-vl-${type}-group`] === group) {
            lastInGroup = false;
        }
        let icon = null;
        let title = null;
        let path = '';
        if (type === 'entity') {
            if (attrib['data-vl-entity']?.startsWith('name:id/')) {
                icon = 'web_person';
                title = 'goToPerson';
                path = '/name/view';
            } else if (attrib['data-vl-entity']?.startsWith('place:id/')) {
                icon = 'web_places';
                title = 'goToPlace';
                path = '/place/view';
            } else if (attrib['data-vl-entity']?.startsWith('subject:id/')) {
                icon = 'web_subject';
                title = 'goToSubject';
                path = '/subject/view';
            } else if (attrib['data-vl-entity']?.startsWith('title:id/')) {
                icon = 'md_book';
                title = 'title';
                path = '';
            }
        }
        if (type === 'glossary') {
            icon = 'glossary';
            title = 'goToGlossaryItem';
            path = '/glossary/view';
        }
        if (!icon) return /*#__PURE__*/ _jsx("span", {
            className: "ocrx_word",
            children: applyTemplates(node?.children || [])
        });
        if (!lastInGroup) {
            return /*#__PURE__*/ _jsx("span", {
                id: `${type}-${group}`,
                className: clsx(S.term, S.group, type == 'glossary' && S.glossary),
                children: node.text
            });
        }
        // hover handler for underlining groups
        let handleMouseEnter, handleMouseLeave;
        if (group) {
            handleMouseEnter = ()=>{
                const elements = document.querySelectorAll(`[id='${type}-${group}']`);
                elements.forEach((el)=>el.classList.add('term-hover'));
            };
            handleMouseLeave = ()=>{
                const elements = document.querySelectorAll(`[id='${type}-${group}']`);
                elements.forEach((el)=>el.classList.remove('term-hover'));
            };
        }
        return /*#__PURE__*/ _jsxs("span", {
            id: group ? `${type}-${group}` : undefined,
            className: clsx(S.term, group && S.group, type == 'glossary' && S.glossary),
            children: [
                node.text,
                /*#__PURE__*/ _jsx("a", {
                    href: `${data.apiurl.href(`${path}/${attrib['data-vl-id'] || attrib['data-vl-glossary']}`)}`,
                    title: i18n.get(title),
                    target: "_blank",
                    onMouseEnter: handleMouseEnter,
                    onMouseLeave: handleMouseLeave,
                    children: /*#__PURE__*/ _jsx(VIcon, {
                        name: icon,
                        style: {
                            marginRight: 0,
                            marginLeft: 2
                        }
                    })
                })
            ]
        });
    }
};
export const hocrWord = {
    match: ({ tag, attrib })=>{
        return tag == 'span' && attrib.className == 'ocrx_word' && !attrib['data-vl-entity'] && !attrib['data-vl-glossary'];
    },
    build: ({ applyTemplates, children, text })=>{
        return /*#__PURE__*/ _jsxs("span", {
            className: S.ocrx_word,
            children: [
                text,
                applyTemplates(children)
            ]
        });
    }
};
export const hocrFootnote = {
    //~ tagMatcher: 'span/footnote',
    match: ({ tag, attrib })=>tag == 'span' && attrib.className == 'footnote',
    build: ({ keys, applyTemplates, node, children })=>{
        const footnotes = keys.byClassName['footnote'].filter(({ tag })=>tag === 'span');
        const index = footnotes.findIndex((e)=>e === node);
        // const handleScrollToFootnote = e => {
        //   e.preventDefault()
        //   const container = document.getElementById('presFulltext')
        //   const target = document.getElementById(`fn-${index + 1}`)
        //   if (container && target) {
        //     const containerRect = container.getBoundingClientRect()
        //     const targetRect = target.getBoundingClientRect()
        //     const scrollTop = container.scrollTop + (targetRect.top - containerRect.top) - 50
        //     container.scrollTo({top: scrollTop, behavior: 'smooth'})
        //     target.classList.add('footnote-blink')
        //     setTimeout(() => {
        //       target.classList.remove('footnote-blink')
        //     }, 1000)
        //   }
        // }
        return /*#__PURE__*/ _jsx(Popup, {
            trigger: /*#__PURE__*/ _jsx("sup", {
                className: S.footnoteRef,
                title: "",
                id: `ref-${index + 1}`,
                children: /*#__PURE__*/ _jsx("a", {
                    href: `#fn-${index + 1}`,
                    style: {
                        fontWeight: 'bolder',
                        margin: '0 1px'
                    },
                    children: index + 1
                })
            }),
            content: applyTemplates(children),
            position: "top center",
            hoverable: true,
            style: {
                pointerEvents: 'auto',
                fontSize: '.85em'
            }
        });
    },
    init: ({ data, keys, applyTemplates })=>{
        let { i18n } = data;
        let footnotes = keys.byClassName['footnote']?.filter(({ tag })=>tag == 'span') || [];
        if (footnotes.length === 0) return null;
        // const handleScrollToRef = i => e => {
        //   e.preventDefault()
        //   const container = document.getElementById('presFulltext')
        //   const target = document.getElementById(`ref-${i + 1}`)
        //   if (container && target) {
        //     const containerRect = container.getBoundingClientRect()
        //     const targetRect = target.getBoundingClientRect()
        //     const scrollTop = container.scrollTop + (targetRect.top - containerRect.top) - 50
        //     container.scrollTo({top: scrollTop, behavior: 'smooth'})
        //     target.classList.add('footnote-blink')
        //     setTimeout(() => {
        //       target.classList.remove('footnote-blink')
        //     }, 1000)
        //   }
        // }
        let items = footnotes.map(({ children }, i)=>{
            return /*#__PURE__*/ _jsxs("li", {
                className: S.hocrFootnote,
                style: {
                    marginBottom: '0.75em'
                },
                id: `fn-${i + 1}`,
                children: [
                    applyTemplates(children),
                    /*#__PURE__*/ _jsx("a", {
                        className: S.jumpBack,
                        style: {
                            marginRight: '4px'
                        },
                        href: `#ref-${i + 1}`,
                        title: i18n.get('backToTop'),
                        children: "↩"
                    })
                ]
            }, i + 1);
        });
        return /*#__PURE__*/ _jsx("div", {
            className: S.hocrFootnotes,
            children: /*#__PURE__*/ _jsx("ol", {
                children: items
            })
        });
    }
};
